import React, { useContext, useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  Divider,
  ListItemAvatar,
  Paper,
  Typography,
  IconButton,
} from "@mui/material";
import { useNavigate } from "react-router";
import Loading from "components/common/Loading";
import GridPage from "components/layout/GridPage";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { useParams } from "react-router-dom";
import useIsVisible from "components/hooks/useIsVisible";
import {
  useDeletePost,
  useEditPost,
  useFetchPost,
  usePinForumPost,
} from "services/forum";
import { useFetchAdvisorProfile } from "services/advisor";
import { FetchAdvisorProfileRequest } from "protogen/advisors_service_pb";
import AddComment, { CommentHandle } from "components/forum/AddComment";
import {
  ForumPost,
  ForumPostType,
  ForumComment,
  FetchForumPostRequest,
} from "protogen/forum_service_pb";
import { ActivityEntityType } from "protogen/common_pb";
import PostComment from "components/forum/PostComment";
import DateDisplay from "components/common/DateDisplay";
import DocumentViewer from "components/editor/DocumentViewer";
import {
  useAdvisorProfilePath,
  UserAvatar,
} from "components/common/CurrentUserAvatar";
import { CurrentUserContext } from "components/context/RequireAuth";
import { isUserAdmin } from "../common/userUtils";
import DocumentEditor from "components/editor/DocumentEditor";
import { RichContent } from "components/editor/utils";
import { SearchContext } from "components/context/SearchContextProvider";
import useIsMobile from "components/hooks/useIsMobile";
import ConfirmationDialog, {
  useConfirmationDialog,
} from "components/common/ConfirmationDialog";
import PostTopics from "components/forum/PostTopics";
import TopicAutocomplete from "components/forum/TopicAutocomplete";
import PostLikesAndComments from "components/forum/PostLikesAndComments";
import { Ellipsis } from "lucide-react";
import { protoInt64 } from "@bufbuild/protobuf";
import BusinessInfo from "components/forum/BusinessInfo";
import PopperMenu, { createMenuOption } from "components/common/PopperMenu";
import NotFound from "components/NotFound";
import LinkRouter from "components/navigation/LinkRouter";
import useTitle from "components/hooks/useTitle";
import { useWebSocketContext } from "components/context/SocketConsumerProvider";
import { UserBadge } from "common/components/badges";
import PostReviewContent, {
  parseReviewMetadata,
} from "components/forum/PostReviewContent";

const arraysContainSame = (a: any[] | null, b: any[] | null) => {
  const a_ = Array.isArray(a) ? a : [];
  const b_ = Array.isArray(b) ? b : [];
  return a_.length === b_.length && a_.every((el) => b_.includes(el));
};

type EditAreaProps = {
  post: ForumPost;
  cancelEdit: () => void;
  onUpdate: () => void;
  editedTitle?: string;
};
const EditArea = ({
  post,
  cancelEdit,
  onUpdate,
  editedTitle,
}: EditAreaProps) => {
  const _initialContent = post.body.payload
    ? JSON.parse(post.body.payload)
    : null;
  const [content, setContent] = useState<RichContent | null>(null);
  const [topics, setTopics] = useState<string[] | null>(null);
  const { request, loading } = useEditPost(() => {
    onUpdate();
  });
  const updatedTopics =
    arraysContainSame(topics, post.topics) || !topics ? [] : topics;
  const changes = content || updatedTopics.length || editedTitle !== post.title;
  const handleSave = () => {
    if (changes) {
      request({
        ref: post.ref,
        title: editedTitle || post.title,
        topics: updatedTopics, // Empty means no changes.
        ...(content && {
          body: {
            contentType: "json",
            payload: content.json,
            textContent: content.text,
            attachments: content.attachments,
          },
        }),
      });
    }
  };
  return (
    <>
      <Box display={"flex"} flexDirection={"row"}>
        <TopicAutocomplete
          setSelected={(s) => setTopics(s)}
          initialSelected={post.topics || []}
          sx={{
            width: "100%",
            maxHeight: "48px",
            ".MuiInputBase-root": {
              minHeight: "48px",
            },
            ".MuiAutocomplete-tag": {
              marginTop: "0px",
            },
            input: {
              marginTop: "0px",
            },
          }}
        />
      </Box>
      <Box sx={{ padding: "16px 16px" }}>
        <DocumentEditor
          initialContent={_initialContent}
          disabled={loading}
          setContent={setContent}
          attachmentsEnabled={true}
          initialAttachments={post.body.attachments}
          linkUnfurlEnabled={true}
          secondaryAction={
            <Button onClick={() => cancelEdit()} variant="outlined">
              Cancel
            </Button>
          }
          primaryAction={
            <Button onClick={handleSave} disabled={loading || !changes}>
              Save
            </Button>
          }
        />
      </Box>
    </>
  );
};

export default () => {
  const navigate = useNavigate();
  const { setForumOnly } = useContext(SearchContext);
  let params = useParams();
  const getAdvisorProfileUrl = useAdvisorProfilePath();
  const isMobile = useIsMobile();
  const { isVisible } = useIsVisible({});
  const postRef = params.postRef!;
  const queryParams = new URLSearchParams(location.search);
  const commentRef = queryParams.get("commentRef") || null;
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [menuOptions, setMenuOptions] = useState<JSX.Element[]>([]);
  const [isPinned, setIsPinned] = useState(false);
  const [editedTitle, setEditedTitle] = useState<string>("");
  const [advisorBlurbContent, setAdvisorBlurbContent] = useState<any>(null);
  const commentBoxRef = useRef<CommentHandle>(null);
  const { registerHandler, unregisterHandler } = useWebSocketContext();
  const { request: fetchAdvisorProfile } = useFetchAdvisorProfile(
    (response) => {
      if (response.advisor?.blurbContent) {
        setAdvisorBlurbContent(response.advisor.blurbContent);
      }
    },
  );
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };
  const handleClose = () => {
    setOpen(false);
  };
  const currentUser = useContext(CurrentUserContext);
  const { request: pinRequest } = usePinForumPost();
  const { request, data, errorCode } = useFetchPost(() => {
    setForumOnly(true);
    //   // Don't thrash the state when it's just image URLs changing.
    //   setEmails(preValue => {
    //     const prevMap = new Map(preValue.map(i => [i.ref, i]))
    //     return r.emails.map(e => {
    //       const prev = prevMap.get(e.ref);
    //       return prev && prev.timestampSec === e.timestampSec ? prev : e;
    //     });
    //   });
  });
  useTitle(data?.post?.title);
  const [editMode, setEditMode] = useState(false);

  const { request: deleteRequest, loading: deleteLoading } = useDeletePost();
  const confirmState = useConfirmationDialog();
  const deletePost = () => {
    if (!deleteLoading) {
      confirmState.openDialog(async () => {
        await deleteRequest({ ref: post.ref });
        navigate("/community");
      });
    }
  };

  useEffect(() => {
    request(new FetchForumPostRequest({ postRef, markAsRead: true }));
    const pushHandler = () => {
      if (isVisible) {
        request(new FetchForumPostRequest({ postRef, markAsRead: true }));
      }
    };
    registerHandler("forum/", pushHandler);
    return () => {
      unregisterHandler("forum/", pushHandler);
    };
  }, [params.postRef]);

  useEffect(() => {
    if (data?.post) {
      setEditedTitle(data.post.title);
      if (data.post.entityType === ActivityEntityType.ADVISOR_JOINED) {
        const metadata = data.post.body?.payload
          ? JSON.parse(data.post.body.payload)
          : {};
        const advisorRef = metadata.advisor_ref;
        if (advisorRef) {
          fetchAdvisorProfile(new FetchAdvisorProfileRequest({ advisorRef }));
        }
      }
    }
  }, [data?.post]);

  const createMenuOptions = (post: ForumPost) => {
    const options = [];
    if (canEdit) {
      options.push(
        createMenuOption(
          "Edit",
          () => {
            setEditMode((e) => !e);
            handleClose();
          },
          "pencil",
        ),
        createMenuOption(
          "Delete",
          () => {
            if (!deleteLoading) {
              deletePost();
            }
            handleClose();
          },
          "trash",
        ),
      );
    }
    if (isUserAdmin(currentUser)) {
      options.push(
        createMenuOption(
          isPinned ? "Unpin" : "Pin",
          () => {
            pinRequest({ postRef: post.ref, unpin: isPinned });
            setIsPinned((p) => !p);
            handleClose();
          },
          isPinned ? "pinOff" : "pin",
        ),
      );
    }
    return options;
  };

  useEffect(() => {
    if (data?.post) {
      setMenuOptions(createMenuOptions(data.post));
    }
  }, [data?.post, isPinned]);

  useEffect(() => {
    if (data?.post) {
      setIsPinned(post.pinnedSec > protoInt64.zero);
    }
  }, [data?.post]);
  if (errorCode === 410)
    return (
      <NotFound
        title={"Post no longer available"}
        message={"The document linked to this post has been deleted."}
      />
    );
  if (errorCode === 404) return <NotFound title={"Post not found"} />;
  if (!data) {
    return <Loading />;
  }

  const onCommentCreation = () => {
    request(new FetchForumPostRequest({ postRef, markAsRead: true }));
  };
  const onUpdated = () => {
    request(new FetchForumPostRequest({ postRef, markAsRead: true }));
    setEditMode(false);
  };

  const sortCommentsByDepth = (
    comments: ForumComment[],
    commentMap: { [key: string]: ForumComment[] },
  ): ForumComment[] => {
    let sorted: ForumComment[] = [];

    const addCommentAndChildren = (comment: ForumComment) => {
      sorted.push(comment);
      const children = commentMap[comment.ref];
      children.forEach((child) => addCommentAndChildren(child));
    };

    comments.forEach((comment) => {
      if (comment.parentRef === "") {
        addCommentAndChildren(comment);
      }
    });

    return sorted;
  };

  const createCommentMap = (comments: ForumComment[]) => {
    const commentMap: { [key: string]: ForumComment[] } = {};
    comments.forEach((comment) => {
      commentMap[comment.ref] = [];
      if (comment.parentRef !== "") {
        commentMap[comment.parentRef].push(comment);
      }
    });
    return commentMap;
  };

  const determineThreadGuidelines = (depthFirstArray: ForumComment[]) => {
    // The UI structure has comments as their own component.  Each comment does not know about
    // it's siblings, parent or children.  However, comments are responsible for rendering
    // their thread guidelines, so we must pass the information in.
    const threadGuidlines: { [key: string]: boolean[] } = {};
    let lines: boolean[] = [];

    const hasSiblingAtDepth = (currentIndex: number, depth: number) => {
      for (let j = currentIndex + 1; j < depthFirstArray.length; j++) {
        if (depthFirstArray[j].depth === depth) {
          return true;
        }
        if (depthFirstArray[j].depth < depth) {
          break;
        }
      }
      return false;
    };

    depthFirstArray.forEach((node, index) => {
      if (node.depth === 0) {
        lines = [];
      } else {
        lines[node.depth - 1] = hasSiblingAtDepth(index, node.depth);
      }

      threadGuidlines[node.ref] = [...lines];
    });

    return threadGuidlines;
  };

  const canEdit =
    currentUser.ref === data.post?.author?.ref || isUserAdmin(currentUser);
  const post = data.post!;
  const comments = data.comments;
  // Comments are returned sorted by timestamp. This is two loops.
  // First to create a map of comment refs to children.
  // createCommentMap - extracted from the sort method because we also use it to determine children/parents.
  // Second to recursively start at top level parents and insert children(replies) in order.  "Depth first insert"
  // This is a "depth first array" a flat array of a tree structure.
  const commentMap = createCommentMap(comments);
  const sortedComments = sortCommentsByDepth(comments, commentMap);
  const threadGuidelines = determineThreadGuidelines(sortedComments);
  const badgeReview =
    post.entityType === ActivityEntityType.BADGE_EARNED
      ? parseReviewMetadata(
          post.body?.payload ? JSON.parse(post.body.payload) : {},
        )
      : null;
  const bodyContent =
    post.entityType === ActivityEntityType.ADVISOR_JOINED
      ? advisorBlurbContent || post.body?.textContent
      : post.body?.payload
        ? JSON.parse(post.body?.payload)
        : post.body?.textContent;

  return (
    <GridPage
      sx={{
        padding: isMobile ? "32px 24px" : "48px 48px 100px",
        margin: "auto",
        maxWidth: "1000px",
      }}
    >
      <Box
        onClick={() => {
          navigate(-1);
        }}
        style={{ cursor: "pointer" }}
      >
        <Typography
          variant="body"
          sx={{
            color: "#198282",
            fontWeight: "600",
            display: "flex",
            alignItems: "center",
          }}
        >
          <ChevronLeftIcon
            width="18px"
            strokeWidth="2.5px"
            style={{ marginRight: "4px" }}
          />
          {"Back to posts"}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          marginBottom: isMobile ? "8px" : "16px",
        }}
      >
        {editMode ? (
          <input
            value={editedTitle}
            onChange={(e) => setEditedTitle(e.target.value)}
            style={{
              flex: "1 1 100%",
              marginTop: "16px",
              fontSize: "2rem",
              fontWeight: "bold",
              border: "none",
              width: "100%",
            }}
          />
        ) : (
          <Typography
            sx={{
              flex: "1 1 100%",
              marginTop: "16px",
            }}
            variant="h1"
            id="tableTitle"
          >
            {data?.post?.title}
          </Typography>
        )}
        {!(isMobile || editMode) && <PostTopics topics={post.topics} />}
      </Box>

      <Paper sx={{ marginBottom: 2, boxShadow: "none" }}>
        <Box sx={{ marginBottom: "18px" }}>
          {editMode ? (
            <EditArea
              post={post}
              cancelEdit={() => {
                setEditMode(false);
                setEditedTitle(post.title);
              }}
              onUpdate={onUpdated}
              editedTitle={editedTitle || ""}
            />
          ) : (
            <>
              <Box sx={{ padding: "0px" }}>
                {badgeReview ? (
                  <PostReviewContent review={badgeReview} fontSize="16px" />
                ) : (
                  <DocumentViewer
                    key={`${post.lastEditSec}-${
                      advisorBlurbContent ? "with-blurb" : "no-blurb"
                    }`}
                    content={bodyContent}
                    attachments={post.body?.attachments}
                    minHeight="75px"
                  />
                )}
              </Box>
              {post.postType === ForumPostType.POST_RECOMMENDATION && (
                <BusinessInfo
                  postRef={post.ref}
                  businessInfo={data.businessInfo!}
                  onUpdated={onUpdated}
                />
              )}
              <Box
                alignItems="flex-start"
                sx={{
                  padding: "24px 0px",
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  <ListItemAvatar>
                    <UserAvatar user={post.author!} />
                  </ListItemAvatar>
                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <LinkRouter
                        to={getAdvisorProfileUrl(post.author?.ref || "")}
                      >
                        <Typography sx={{ fontWeight: "700" }}>
                          {post.author?.displayName}
                        </Typography>
                      </LinkRouter>
                      {post.author?.badgeCounts && (
                        <UserBadge
                          badges={post.author.badgeCounts}
                          variant="inline"
                          profileUrl={getAdvisorProfileUrl(
                            post.author?.ref || "",
                          )}
                        />
                      )}
                    </Box>
                    <Typography
                      component="span"
                      variant="bodySmall"
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        gap: "4px",
                        fontSize: "14px",
                        color: "#6B6E7B",
                      }}
                    >
                      <DateDisplay
                        date={new Date(Number(post.createdSec) * 1000)}
                        sx={{
                          fontSize: "14px",
                          color: "#6B6E7B",
                        }}
                      />
                      {post.createdSec !== post.lastEditSec && (
                        <span>{" (edited)"}</span>
                      )}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              {!isMobile && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Box sx={{ display: "flex", gap: "12px" }}>
                    <PostLikesAndComments
                      post={post}
                      onCommentClick={() => commentBoxRef?.current?.focus()}
                    />
                    {canEdit && (
                      <Box>
                        <IconButton ref={anchorRef} onClick={handleToggle}>
                          <Ellipsis size={20} />
                        </IconButton>
                        <PopperMenu
                          open={open}
                          anchorRef={anchorRef}
                          handleClose={handleClose}
                        >
                          {menuOptions}
                        </PopperMenu>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
              {isMobile && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "start",
                    gap: "12px",
                  }}
                >
                  <PostTopics topics={post.topics} />
                  <PostLikesAndComments post={post} />
                  {canEdit && (
                    <Box>
                      <IconButton ref={anchorRef} onClick={handleToggle}>
                        <Ellipsis size={20} />
                      </IconButton>
                      <PopperMenu
                        open={open}
                        anchorRef={anchorRef}
                        handleClose={handleClose}
                      >
                        {menuOptions}
                      </PopperMenu>
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
        <Divider />
        <Box sx={{ marginTop: "24px" }}>
          {/* Badge posts are only commentable when they carry review content */}
          {(post.entityType !== ActivityEntityType.BADGE_EARNED ||
            !!badgeReview) && (
            <AddComment
              postRef={postRef}
              onCreate={onCommentCreation}
              ref={commentBoxRef}
            />
          )}
          <Box
            sx={{
              margin: "0",
              display: "flex",
              flexDirection: "column",
              gap: "24px",
            }}
          >
            {sortedComments.map((comment) => (
              <PostComment
                key={comment.ref}
                comment={comment}
                post={post}
                highlighted={commentRef === comment.ref}
                onCommentCreation={onCommentCreation}
                hasParent={comment.parentRef !== ""}
                hasChildren={commentMap[comment.ref].length > 0}
                threadGuidelines={threadGuidelines[comment.ref]}
              />
            ))}
          </Box>
        </Box>
      </Paper>
      <ConfirmationDialog
        title="Delete post"
        noIcon={true}
        content="Are you sure you want to delete this post?"
        {...confirmState.dialogProps}
      />
    </GridPage>
  );
};
